import React, { useEffect, useState, useCallback } from 'react';
import { SelectAsync } from '@upsales/components';
import { SelectItem } from '@upsales/components/Utils/selectHelpers';
import { makeCancelable } from 'App/babel/helpers/promise';
import RequestBuilder, { comparisonTypes } from 'Resources/RequestBuilder';
import { BuildFilters } from 'Resources/RequestBuilder';
import { Attr } from 'App/babel/attributes/Attribute';
import logError from 'App/babel/helpers/logError';
import T from 'Components/Helpers/translate';
import { useSelectLanguage } from '../Inputs/Selects/selectHelpers';

type RawObject<TitleField extends string> = {
	[key in TitleField]: string;
} & {
	id: string;
};

type Resource<TitleField extends string = 'name'> = {
	find: (filter: BuildFilters) => Promise<{ data: RawObject<TitleField>[]; metadata: { total: number } }>;
	attr: {
		id: Attr;
		name: Attr;
	};
};

export type ResourceSelectProps<TitleField extends string = 'name'> = Omit<
	React.ComponentProps<typeof SelectAsync>,
	'fetcher' | 'value' | 'resource'
> & {
	resource: Resource<TitleField>;
	value: SelectItem | SelectItem[] | null;
	modifyRb?: (rb: RequestBuilder) => void;
	extraOptions?: SelectItem[];
	limit?: number;
	fields?: string[];
	extraFields?: string[];
	titleField?: TitleField;
};

const mapper = <TitleField extends string>(object: RawObject<TitleField>, titleField: TitleField): SelectItem => ({
	...object,
	title: object[titleField]
});

export function ResourceSelect<TitleField extends string = 'name'>(props: ResourceSelectProps<TitleField>) {
	const {
		resource,
		value,
		modifyRb,
		titleField = 'name',
		extraFields,
		limit = 500,
		language,
		...selectProps
	} = props;
	const finalLanguage = useSelectLanguage(language);

	// useCallback because otherwise SelectAsync will fetch on every render after the first search
	const fetcher = useCallback(
		async (searchTerm: string): Promise<SelectItem[]> => {
			const rb = new RequestBuilder();
			const nameAttribute = resource?.attr?.name ?? { field: titleField };

			if (searchTerm) {
				rb.addFilter(nameAttribute, comparisonTypes.Search, searchTerm);
			}

			rb.addSort(nameAttribute, true);

			rb.limit = limit;
			rb.fields = ['id', titleField, ...(extraFields ?? [])];

			if (modifyRb) {
				modifyRb(rb);
			}

			return resource
				.find(rb.build())
				.then(({ data }) => data.map(obj => mapper(obj, titleField as TitleField)))
				.catch(error => {
					console.log(error);
					return [];
				});
		},
		[resource, extraFields, titleField, limit, modifyRb]
	);

	return (
		<SelectAsync
			fetcher={fetcher}
			fetchOnOpen={true}
			value={value}
			onClear={() => props.onChange?.(null as any)}
			language={finalLanguage}
			{...selectProps}
		/>
	);
}

export type ResourceMultiSelectProps = Omit<ResourceSelectProps, 'value' | 'onChange' | 'onClear' | 'onRemove'> & {
	value: SelectItem[];
	onChange: (value: SelectItem[]) => void;
};

export const ResourceMultiSelect = (props: ResourceMultiSelectProps) => {
	const { resource, onChange, value, multi, ...selectProps } = props;

	const innerOnChange = (newValue: SelectItem | null) => {
		let finalValue: SelectItem[] = [];
		if (newValue) {
			finalValue = [...value, newValue];
		} else {
			finalValue = [];
		}

		onChange(finalValue);
	};

	return (
		<ResourceSelect
			resource={resource}
			multi
			value={value}
			onChange={innerOnChange}
			onClear={() => onChange([])}
			onRemove={id => {
				const newValue = value.filter(item => item.id !== id);
				onChange(newValue);
			}}
			{...selectProps}
		/>
	);
};

type ResourceIdSelectPropsBase = Omit<ResourceSelectProps, 'value' | 'onChange'> & {
	titleField?: string;
	extraOptions?: SelectItem[];
};

export type ResourceIdSelectPropsSingle = ResourceIdSelectPropsBase & {
	multi?: false;
	value: SelectItem['id'] | null;
	onChange: (value: SelectItem['id'] | null) => void;
};

export type ResourceIdSelectPropsMulti = ResourceIdSelectPropsBase & {
	multi: true;
	value: SelectItem['id'][];
	onChange: (value: SelectItem['id'][]) => void;
};

export type ResourceIdSelectProps = ResourceIdSelectPropsSingle | ResourceIdSelectPropsMulti;

export function ResourceIdSelect(props: ResourceIdSelectProps) {
	const { resource, onChange, value, multi, titleField = 'name', extraOptions = [], ...selectProps } = props;
	const [_value, setValue] = useState<SelectItem | SelectItem[] | null>(multi ? [] : null);

	useEffect(() => {
		if (value === null || (Array.isArray(value) && value.length === 0)) {
			setValue(multi ? [] : null);
		} else {
			const rb = new RequestBuilder();
			rb.addSort(resource.attr.name, true);
			rb.addFilter(resource.attr.id, comparisonTypes.Equals, value);

			const cancelable = makeCancelable(resource.find(rb.build()));

			cancelable.promise
				.then(({ data: reqData }) => {
					const data = [...reqData.map(obj => mapper(obj, titleField)), ...extraOptions]; // extraOptions is used in the case when the api-fetcher has not yet gotten a newly created item
					const idMap = data.reduce<{ [id in SelectItem['id']]?: SelectItem }>((idMap, object) => {
						idMap[object.id] = object;
						return idMap;
					}, {});

					const unknown = {
						id: value as string,
						name: T('default.unknown'),
						title: T('default.unknown')
					};

					let _value: SelectItem | SelectItem[] | null = null;

					if (multi) {
						_value = (value as SelectItem['id'][]).map(id => idMap[id] || unknown);
					} else {
						// The unknown case is if you do not have access to the object
						_value = idMap[value as string] || unknown;
					}
					setValue(_value);
				})
				.catch((error: unknown) => logError(error, 'Error in ResourceIdSelect useEffect'));

			return () => cancelable.cancel();
		}
	}, [value]);

	const _onChange = (newValue: SelectItem) => {
		if (multi) {
			const currentIds = value as SelectItem['id'][];
			const currentItems = _value as SelectItem[];

			// Toggle selection: add if not present, remove if already selected
			const isSelected = currentIds.includes(newValue.id);

			const newIds = isSelected ? currentIds.filter(id => id !== newValue.id) : [...currentIds, newValue.id];
			const newItems = isSelected
				? currentItems.filter(item => item.id !== newValue.id)
				: [...currentItems, newValue];

			setValue(newItems);
			onChange(newIds);
		} else {
			setValue(newValue);
			onChange(newValue.id);
		}
	};

	const onRemove = (id: SelectItem['id']) => {
		if (multi) {
			const itemToRemove = (_value as SelectItem[]).find(item => item.id === id);
			if (itemToRemove) {
				_onChange(itemToRemove);
			}
		}
	};

	const onClear = () => {
		if (multi) {
			setValue([]);
			onChange([]);
		} else {
			setValue(null);
			onChange(null);
		}
	};

	return (
		<ResourceSelect
			resource={resource}
			value={_value}
			multi={multi}
			onChange={_onChange}
			onRemove={onRemove}
			onClear={onClear}
			{...selectProps}
		/>
	);
}

export default ResourceSelect;
